import { useProfileStore } from "../../../stores/UserStore";
import AvatarEditor from "../account/components/AvatarEditor";
import { updateProfile, updateSelf } from "../../../api/UserAPI";
import { useEffect, useState } from "react";
import { useLanguage } from "../../../stores/LanguageStore";
import { notifications } from "@mantine/notifications";
import {
  Accordion,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  PasswordInput,
  Radio,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import PhoneNumberInput from "../../../components/Selectors/PhoneNumberInput";
import CountrySelector from "../../../components/Selectors/CountrySelector";
import { IconDeviceFloppy, IconRestore } from "@tabler/icons-react";
import timezones from "../account/components/timezones.json";
import PasswordChanger from "../account/components/PasswordChanger";
import DisableAccount from "../admin/DisableAccount";
import DeleteAccount from "../admin/DeleteAccount";
import { useNavigate } from "react-router-dom";
import StopSubscription from "../admin/StopSubscription";

const SettingsProfile = () => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);
  const [loading, setLoading] = useState(false);
  const [pwdConfirm, setPwdConfirm] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    data.emails_notification = data?.emails_notification?.split(",") || [];
    setPwdConfirm(false);
    if (data?.password_confirm !== data.password) {
      setPwdConfirm(true);
      return document.getElementById("pwd-confirm").focus();
    }

    data.timezone = data.timezone || null

    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([key, value]) => value !== "")
    );

    setLoading(true);
    updateProfile(profile.id, data)
      .then((res) => {
        updateSelf();
        setEmailChanged(false);
        notifications.show({
          message: lang.profile.edited,
          color: "tertiary.8",
        });

        if (profile.language !== filteredData.language) {
          location.reload();
        }
      })
      .finally(() => setLoading(false));
  };

  const navigate = useNavigate();
  const onDisableSuccess = () => {
    setTimeout(() => {
      navigate("/login", { replace: true });
      localStorage.clear();
    }, 500);
  };

  useEffect(() => {
    updateSelf();
  }, []);

  return (
    <>
      <Stack
        component={"form"}
        id="profile-form"
        onSubmit={handleSubmit}
        align="start"
        gap={"lg"}
        style={{ overflow: "auto", position: "relative" }}
        pr={32}
      >
        <LoadingOverlay
          visible={loading}
          overlayProps={{ blur: 2 }}
          w={"100%"}
          h={"100%"}
        />
        <Stack style={{ overflow: "auto" }} gap={20} pb={16} w={"100%"}>
          <Paper w={"100%"} component={Stack} p={"md"} px={"xl"} radius={"md"}>
            <AvatarEditor profile={profile} onSuccess={updateSelf} />
          </Paper>

          <Paper
            // withBorder
            component={Stack}
            w={"100%"}
            p={"md"}
            px={"xl"}
            radius={"md"}
          >
            <Text fw={600} size="xl">
              {lang.profile.personal_infos}
            </Text>
            <Group gap={"xl"}>
              <TextInput
                name="first_name"
                label={lang.profile.first_name}
                defaultValue={profile?.first_name}
                w={250}
                required
                size="md"
              />
              <TextInput
                name="last_name"
                label={lang.profile.last_name}
                defaultValue={profile?.last_name}
                w={250}
                required
                size="md"
              />
              <Radio.Group
                name="sex"
                label={lang.signup.civility}
                w={250}
                required
                defaultValue={profile?.sex}
                mt={-6}
                size="md"
              >
                <Group mt="xs">
                  <Radio
                    value="male"
                    label={lang.profile.civility_male}
                    size="sm"
                  />
                  <Radio
                    value="female"
                    label={lang.profile.civility_female}
                    size="sm"
                  />
                </Group>
              </Radio.Group>
            </Group>
            <Group gap={"xl"} align="start">
              <Stack gap={"xs"}>
                <TextInput
                  type="email"
                  name="email"
                  label={lang.profile.mail}
                  placeholder={lang.profile.mail}
                  defaultValue={profile?.email}
                  w={250}
                  required
                  onChange={(e) =>
                    setEmailChanged(profile?.email !== e.target.value)
                  }
                  size="md"
                />

                {emailChanged && (
                  <Text size="xs" c={"red"}>
                    * {lang.profile.mail_helper}
                  </Text>
                )}
              </Stack>

              <PhoneNumberInput
                defaultCountry={profile?.mobile_phone_country || "FR"}
                defaultValue={profile?.mobile_phone}
                name={"mobile_phone"}
                // label={lang.profile.mobile_phone}
                label={lang.unsubscribers.columns.phone}
                w={250}
                size="md"
              />
              <Select
                label={lang.profile.language}
                placeholder={lang.profile.language}
                name="language"
                defaultValue={profile?.language}
                allowDeselect={false}
                w={250}
                size="md"
                data={[
                  { label: "English", value: "ENG" },
                  { label: "Français", value: "FRA" },
                  { label: "Español", value: "SPA" },
                  { label: "Italiano", value: "ITA" },
                  { label: "Allemand", value: "DEU" },
                ]}
              />
            </Group>

            {emailChanged && (
              <PasswordInput
                label={lang.profile.actual_password}
                placeholder={lang.profile.actual_password}
                name="current_password"
                size="md"
                required
              />
            )}

            <Group gap={"xl"} wrap="nowrap" align="start">
              <TextInput
                label={lang.profile.company}
                placeholder={lang.profile.company_desc}
                defaultValue={profile?.company}
                name="company"
                size={"md"}
                w={250}
              />
              <TextInput
                label={lang.profile.job_title}
                placeholder={lang.profile.job_title_desc}
                defaultValue={profile?.job_title}
                name="job_title"
                size={"md"}
                w={250}
              />
              <Textarea
                label={lang.signup.activity_description}
                placeholder={lang.signup.activity_description_placeholder}
                defaultValue={profile?.activity_description}
                name="activity_description"
                maxLength={500}
                resize="vertical"
                rows={2}
                size="md"
                flex={1}
              />
            </Group>

            {/* <Group gap={"xl"}>
          </Group> */}

            {/* <TextInput
            defaultValue={profile?.phone}
            name={"phone"}
            label={lang.profile.phone}
            w={250}
            description={lang.profile.with_zero}
            withCountrySelector={false}
          /> */}
          </Paper>

          <Paper component={Stack} w={"100%"} p={"md"} px={"xl"} radius={"md"}>
            <Text fw={600} size="xl">
              {lang.profile.addresses}
            </Text>

            <Group gap={"xl"} wrap="nowrap">
              <TextInput
                name="address"
                label={lang.profile.address}
                placeholder={lang.profile.address}
                defaultValue={profile?.address}
                w={"100%"}
                size="md"
              />
              <TextInput
                label={lang.profile.postal_code}
                placeholder={lang.profile.postal_code}
                name="zip_code"
                defaultValue={profile?.zip_code}
                w={"100%"}
                size="md"
              />
            </Group>

            <Group gap={"xl"} wrap="nowrap">
              <TextInput
                name="city"
                label={lang.profile.city}
                placeholder={lang.profile.city}
                defaultValue={profile?.city}
                w={"100%"}
                size="md"
              />
              <CountrySelector
                label={lang.profile.country}
                placeholder={lang.profile.country}
                name="country"
                defaultValue={profile?.country}
                w={"100%"}
                size="md"
              />
            </Group>
          </Paper>

          <Accordion variant="contained" radius={"md"}>
            <Accordion.Item
              value="password"
              style={{ border: "none" }}
              bg={"#fff"}
            >
              <Accordion.Control bg={"#fff"} px={"xl"}>
                <Text size={"xl"} fw={600}>
                  {lang.profile.password}
                </Text>
              </Accordion.Control>
              <Accordion.Panel
                bg={"#fff"}
                style={{ borderRadius: 12 }}
                px={"md"}
              >
                <PasswordChanger pwdConfirm={pwdConfirm} />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Accordion variant="contained" radius={"md"}>
            <Accordion.Item
              value="advanced"
              style={{ border: "none" }}
              bg={"#fff"}
            >
              <Accordion.Control bg={"#fff"} px={"xl"}>
                <Text size={"xl"} fw={600}>
                  {lang.profile.advanced}
                </Text>
              </Accordion.Control>
              <Accordion.Panel
                bg={"#fff"}
                style={{ borderRadius: 12 }}
                px={"md"}
              >
                <Group gap={"xl"} align="start" wrap="nowrap" mb={24}>
                  <Select
                    data={timezones}
                    defaultValue={profile?.timezone}
                    w={"100%"}
                    label={lang.profile.timezone.label}
                    placeholder={lang.profile.country}
                    description={lang.profile.timezone_text}
                    name="timezone"
                    searchable
                    size="md"
                    clearable
                  />
                  <TagsInput
                    defaultValue={profile?.emails_notification || []}
                    w={"100%"}
                    description={lang.profile.mail_notif_text}
                    label={lang.profile.mail_notification}
                    placeholder={lang.profile.mail_notification}
                    name="emails_notification"
                    size="md"
                  />
                </Group>

                <Group gap={"xl"} align="start" wrap="nowrap">
                  <Select
                    label={lang.profile.list_language}
                    placeholder={lang.profile.language}
                    name="contact_list_language"
                    defaultValue={profile?.contact_list_language}
                    description={lang.profile.list_language_text}
                    w={"100%"}
                    data={[
                      { label: "English", value: "ENG" },
                      { label: "Français", value: "FRA" },
                      { label: "Español", value: "SPA" },
                    ]}
                    size="md"
                  />

                  <CountrySelector
                    label={lang.profile.list_country}
                    placeholder={lang.profile.country}
                    name="contact_list_country"
                    description={lang.profile.list_country_text}
                    defaultValue={profile?.contact_list_country}
                    w={"100%"}
                    size="md"
                  />
                </Group>

                <Divider mt={"xl"} mb={"xl"} />
                <Text mt={"md"} w={"70%"}>
                  {lang.profile.deletion_text}
                </Text>

                <Group gap={"md"} mt={"md"}>
                  <StopSubscription profile={profile} />

                  <DisableAccount
                    profile={profile}
                    onSuccess={onDisableSuccess}
                  />

                  <DeleteAccount
                    profile={profile}
                    onSuccess={onDisableSuccess}
                  />
                </Group>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* <Paper
          withBorder
          component={Stack}
          w={"fit-content"}
          p={"md"}
          px={"xl"}
          radius={"md"}
        >
          <Text fw={600} size="md" mb={-8}>
            {lang.profile.advanced}
          </Text>
        </Paper>

        <Paper
          withBorder
          component={Stack}
          w={"fit-content"}
          p={"md"}
          px={"xl"}
          radius={"md"}
        >
          <Text fw={600} size="md" mb={-8}>
            {lang.profile.defaults}
          </Text>
        </Paper> */}

          {/* <Paper
          withBorder
          component={Stack}
          w={"100%"}
          p={"md"}
          px={"xl"}
          radius={"md"}
        >
          <StopSubscription profile={profile} />
        </Paper> */}
        </Stack>
      </Stack>

      <Paper
        component={Group}
        justify="space-between"
        p={16}
        px={"xl"}
        pos={"sticky"}
        bottom={0}
        mr={32}
        bg={"#fff"}
        withBorder
      >
        <Button
          variant="outline"
          type="reset"
          form="profile-form"
          size="md"
          leftSection={<IconRestore size={18} />}
          disabled={loading}
          w={200}
        >
          {lang.profile.reset_button_label}
        </Button>
        <Button
          variant="filled"
          form="profile-form"
          type="submit"
          loading={loading}
          leftSection={<IconDeviceFloppy size={18} />}
          size="md"
          w={200}
        >
          {lang.profile.save_button_label}
        </Button>
      </Paper>
    </>
  );
};

export default SettingsProfile;
